/****************************************************
 * Module Name:  createNestedExcelFile.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import * as XLSX from "sheetjs-style";

export const createNestedExcelFile = async (
    outerData,
    innerDataMap,
    outerColumns,
    innerColumns,
    filename,
    startInnerTableColumnFrom,
) => {
    const COLORS = {
        headerBg: "304674",
        innerHeaderBg: "d8e1e8",
        headerText: "FFFFFFFF",
        default: "000000",
    };

    const DEFAULT_BORDER = {
        top: { style: "thin", color: { rgb: COLORS.default } },
        bottom: { style: "thin", color: { rgb: COLORS.default } },
        left: { style: "thin", color: { rgb: COLORS.default } },
        right: { style: "thin", color: { rgb: COLORS.default } },
    };

    const DEFAULT_FONT = "Cambria";

    const DEFAULT_FONT_SIZE = 11;
    // Helper to add headers    
    const addHeaders = (columns, startCol, rowIndex, worksheet, isInner) => {
        columns.forEach((col, i) => {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: startCol + i });
            worksheet[cellRef] = {
                t: "s",
                v: col.label,
                s: {
                    font: { bold: true, name: DEFAULT_FONT, sz: DEFAULT_FONT_SIZE, color: { rgb: isInner ? COLORS.default : COLORS.headerText } },
                    alignment: { horizontal: "center" },
                    fill: {
                        patternType: "solid",
                        fgColor: {
                            rgb: isInner ? COLORS.innerHeaderBg : COLORS.headerBg
                        }
                    },
                    border: DEFAULT_BORDER,
                },
            };
        });
    };

    const addRow = (row, columns, startCol, rowIndex, worksheet) => {

        columns.forEach((col, i) => {

            const val = row[col.key];
            const key = col.key;
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: startCol + i });

            let isBold = false;

            // logic for making cutpolsym bold
            if (key === "CUT" || key === "POL" || key === "SYM") {
                isBold = ["ID", "EX"].includes(row["CUT"]) &&
                    ["ID", "EX"].includes(row["POL"]) &&
                    ["ID", "EX"].includes(row["SYM"]);
            }

            const isNumeric = typeof val === "number" ? true : false;
            worksheet[cellRef] = {
                t: typeof val === "number" ? "n" : "s",
                v: val !== undefined && val !== null ? val : "",
                l: {
                    Target: key === "MEDIA" ? val : null,
                    Tooltip: "Click to open images & videos",
                },
                s: {
                    font: {
                        name: DEFAULT_FONT,
                        sz: DEFAULT_FONT_SIZE,
                        bold: ((key === "CUT" || key === "POL" || key === "SYM") && isBold) || key === "DIS" || key === "NO OF OFFER",
                        color: {
                            rgb: key === "MEDIA" ? "0061FF" : key === "DIS" ? "ED2939" : COLORS.default,
                        },
                        underline: key === "MEDIA" ? true : false,

                    },
                    border: DEFAULT_BORDER,
                    alignment: { horizontal: isNumeric ? "right" : "center" },
                },
            };
        });
    };

    const addEmptyRow = (totalCols, startCol, rowIndex, worksheet) => {
        for (let i = 0; i < totalCols; i++) {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: startCol + i });

            worksheet[cellRef] = {
                t: "s",
                v: "", // Empty string for a blank cell
                s: {
                    font: { name: DEFAULT_FONT, sz: DEFAULT_FONT_SIZE },
                    alignment: { horizontal: "left" },
                },
            };
        }
    };

    let worksheet = XLSX.utils.aoa_to_sheet([]);

    const totalCols = Math.max(outerColumns.length + innerColumns.length);

    let currentRow = 0;

    outerData.forEach(parentRow => {
        addHeaders(outerColumns, 0, currentRow, worksheet, false);
        currentRow++;

        addRow(parentRow, outerColumns, 0, currentRow, worksheet);
        currentRow++;

        addHeaders(innerColumns, startInnerTableColumnFrom, currentRow, worksheet, true);
        currentRow++;

        let innerTableData = innerDataMap.get(parentRow.ID); // make sure u send data with "ID" as a key

        if (innerTableData && innerTableData.length > 0) {
            innerTableData.forEach(iData => {
                addRow(iData, innerColumns, startInnerTableColumnFrom, currentRow, worksheet);
                currentRow++;
            })
        }

        addEmptyRow(totalCols, 0, currentRow, worksheet);
        currentRow++;
    });

    if (currentRow > 0) {
        const totalCols = outerColumns.length + innerColumns.length;
        worksheet["!ref"] = XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: currentRow - 1, c: totalCols - 1 },
        });
    } else {
        console.warn("No rows written, skipping worksheet !ref assignment.");
    }

    function getAutoColumnWidths(columns, rows) {
        return columns.map(col => {
            const header = String(col.label || col.key).trim();
            const maxContentWidth = Math.max(
                header.length,
                ...rows.map(row => {
                    const value = row[col.key];
                    return value !== undefined && value !== null
                        ? String(value).trim().length
                        : 0;
                })
            );
            return { wch: maxContentWidth + 1 }; // add some padding
        });
    }

    // Get individual widths
    const outerWidths = getAutoColumnWidths(outerColumns, outerData);
    const innerDataFlat = Array.from(innerDataMap.values()).flat();
    const innerWidths = getAutoColumnWidths(innerColumns, innerDataFlat);

    // Compute total columns needed
    const totalColss = Math.max(
        outerWidths.length,
        startInnerTableColumnFrom + innerWidths.length
    );

    // Initialize final column widths
    const finalCols = Array.from({ length: totalColss }, () => ({ wch: 2 }));

    // Apply outer widths
    for (let i = 0; i < outerWidths.length; i++) {
        finalCols[i] = { wch: outerWidths[i].wch };
    }

    // Apply inner widths with merging logic
    for (let i = 0; i < innerWidths.length; i++) {
        const index = startInnerTableColumnFrom + i;
        const innerWch = innerWidths[i].wch;

        if (finalCols[index]) {
            // Compare and take max
            finalCols[index].wch = Math.max(finalCols[index].wch, innerWch);
        } else {
            finalCols[index] = { wch: innerWch };
        }
    }

    // Apply to worksheet
    worksheet["!cols"] = finalCols;

    // Write file
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "Nested Report");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });

    // Trigger browser download
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
